import React, { useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg } from "../../constants/styles";
import { gradCardStyle, gradLift, glassPill, glassTile, isLightTone, SLATE_INK } from "../../constants/gradCard";
import Avatar from "../common/Avatar";

// Dialer href — keep digits + a leading +, drop spaces/punctuation.
const telHref = (v) => "tel:" + String(v || "").replace(/[^\d+]/g, "");

function ServicePersonsPage() {
    const { t, h, sps, setSps, go, flash, showConfirm, setSelTid, orgId } = useContext(Ctx);

    // One-tap activate/deactivate without going into Edit. Optimistic
    // local update; on server failure we roll the local state back so
    // the badge doesn't lie to the landlord about what's actually active.
    const toggleActive = async (sp) => {
        const next = sp.active ? 0 : 1;
        const optimistic = { ...sp, active: next };
        setSps(sps.map(x => x.id === sp.id ? optimistic : x));
        try {
            const res = await api.saveServicePerson(optimistic);
            if (!res || res.success === false) throw new Error(res?.error || "save failed");
            flash(next ? (t.spActivated || "Service Person activated") : (t.spDeactivated || "Service Person deactivated"));
        } catch (e) {
            // Roll back local state on failure.
            setSps(sps.map(x => x.id === sp.id ? sp : x));
            flash("Could not update — try again", "error");
        }
    };

    // SPs are now platform-wide (sync returns own + top-10 nearest from
    // other orgs). Split for display so the landlord understands which
    // ones they can manage vs which are read-only marketplace entries.
    const ownSps = sps.filter(s => !s.org_id || s.org_id === orgId);
    const externalSps = sps.filter(s => s.org_id && s.org_id !== orgId);
    const activeCount = ownSps.filter(s => s.active).length;
    const inactiveCount = ownSps.length - activeCount;

    return <div style={{ paddingTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0 }}>{t.servicePersons}</h2>
            <button data-coach="sp-add" onClick={() => go("addServicePerson")} style={{ background: h.ac, border: "none", color: "#fff", padding: "8px 14px", borderRadius: 10, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>+ {t.add}</button>
        </div>

        {ownSps.length > 0 && (
            <div style={{ color: h.sc, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
                Yours · {activeCount} Active
                {inactiveCount > 0 && <span style={{ marginLeft: 8 }}>· {inactiveCount} Inactive</span>}
            </div>
        )}

        {sps.length === 0 && <div style={{ color: h.sc, textAlign: "center", padding: 40, fontSize: 13 }}>{t.noData}</div>}

        {/* OWN SPs — full controls (Edit / Activate / Delete) */}
        <div data-coach="sp-list">
        {ownSps.map(sp => {
            // Smart state: active = bold green, inactive = muted slate
            // (greyed out, dark text). Buttons become glass tiles on the
            // vivid card so their colors still read.
            const tone = sp.active ? "green" : "slate";
            const light = isLightTone(tone);
            // Slate cards use FIXED dark ink — dark-mode theme tokens are
            // near-white and vanished on the light grey gradient.
            const tx = light ? SLATE_INK.tx : "#fff";
            const sub = light ? SLATE_INK.sub : "rgba(255,255,255,0.85)";
            const phone = light ? h.ac : "#fff";
            return <div key={sp.id}
                onMouseOver={(e) => gradLift(e, tone, true)}
                onMouseOut={(e) => gradLift(e, tone, false)}
                style={{ ...crd(h), ...gradCardStyle(tone) }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <Avatar src={sp.avatar_url} name={sp.name} size={40} bg={light ? h.ac : "rgba(255,255,255,0.25)"} ring={light ? undefined : "2px solid rgba(255,255,255,0.6)"} />
                    <div style={{ minWidth: 0 }}>
                        <div style={{ color: tx, fontWeight: 600, fontSize: 14 }}>{sp.name}</div>
                        <div style={{ color: sub, fontSize: 12, marginTop: 2 }}>
                            <a
                                href={telHref(sp.ph)}
                                title={`Call ${sp.name}`}
                                style={{ color: phone, fontSize: 14, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                            >
                                {"📞"} {sp.ph}
                            </a>
                            <span style={{ color: sub }}> · {sp.skill || "General"}</span>
                        </div>
                    </div>
                </div>
                <span style={light ? bdg(sp.active ? `${h.gn}20` : `${h.rd}20`, sp.active ? h.gn : h.rd) : glassPill}>
                    {sp.active ? "Active" : "Inactive"}
                </span>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button onClick={() => { setSelTid(sp.id); go("editServicePerson"); }} style={light ? { flex: 1, padding: 8, borderRadius: 8, border: "none", background: `${h.ac}15`, color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600 } : glassTile(h.ac)}>{"✏️ " + t.edit}</button>
                <button
                    onClick={() => toggleActive(sp)}
                    style={light
                        ? { flex: 1, padding: 8, borderRadius: 8, border: "none", background: sp.active ? `${h.wn}15` : `${h.gn}15`, color: sp.active ? h.wn : h.gn, cursor: "pointer", fontSize: 12, fontWeight: 600 }
                        : glassTile(sp.active ? h.wn : h.gn)}>
                    {sp.active ? (t.deactivate || "⏸ Deactivate") : (t.activate || "▶ Activate")}
                </button>
                <button onClick={() => showConfirm(t.deleteSpConfirm, async () => { await api.deleteServicePerson(sp.id); setSps(sps.filter(x => x.id !== sp.id)); flash(t.spDeleted); })} style={light ? { flex: 1, padding: 8, borderRadius: 8, border: "none", background: `${h.rd}15`, color: h.rd, cursor: "pointer", fontSize: 12, fontWeight: 600 } : glassTile(h.rd)}>{"✕ " + t.delete}</button>
            </div>
        </div>; })}

        {/* EXTERNAL SPs — read-only marketplace listings. The landlord
            can view + assign jobs but can't Edit / Delete (server enforces
            this too via the org_id check on POST/DELETE). */}
        {externalSps.length > 0 && (
            <>
                <div style={{ color: h.sc, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, marginTop: 16, marginBottom: 8 }}>
                    {externalSps.some(s => s.distance_km != null)
                        ? `Nearby Service Persons (within 25 km) · ${externalSps.length}`
                        : `Marketplace Service Persons · ${externalSps.length}`}
                </div>
                {externalSps.map(sp => {
                    // Marketplace (shared) SP: active = indigo brand card,
                    // inactive = muted slate. Read-only — no action buttons.
                    const tone = sp.active ? "indigo" : "slate";
                    const light = isLightTone(tone);
                    const tx = light ? SLATE_INK.tx : "#fff";
                    const sub = light ? SLATE_INK.sub : "rgba(255,255,255,0.85)";
                    const phone = light ? h.ac : "#fff";
                    return (
                    <div key={sp.id}
                        onMouseOver={(e) => gradLift(e, tone, true)}
                        onMouseOut={(e) => gradLift(e, tone, false)}
                        style={{ ...crd(h), ...gradCardStyle(tone) }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                <Avatar src={sp.avatar_url} name={sp.name} size={40} bg={light ? h.ac : "rgba(255,255,255,0.25)"} ring={light ? undefined : "2px solid rgba(255,255,255,0.6)"} />
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ color: tx, fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                        {sp.name}
                                        {sp.distance_km != null && (
                                            <span style={light
                                                ? { background: `${h.gn}15`, color: h.gn, padding: "1px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }
                                                : { background: "rgba(255,255,255,0.25)", color: "#fff", padding: "1px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }}>
                                                📍 {sp.distance_km} km away
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ color: sub, fontSize: 12, marginTop: 2 }}>
                                        <a
                                            href={telHref(sp.ph)}
                                            title={`Call ${sp.name}`}
                                            style={{ color: phone, fontSize: 14, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                                        >
                                            {"📞"} {sp.ph}
                                        </a>
                                        <span style={{ color: sub }}> · {sp.skill || "General"}</span>
                                    </div>
                                </div>
                            </div>
                            <span style={light ? bdg(`${h.ac}15`, h.ac) : glassPill}>Marketplace</span>
                        </div>
                        {/* Compact one-liner explaining the read-only state. */}
                        <div style={{ color: light ? SLATE_INK.sub : "rgba(255,255,255,0.8)", fontSize: 11, fontStyle: "italic", marginTop: 8 }}>
                            ℹ️ Shared SP — you can assign jobs, not edit
                        </div>
                    </div>
                    );
                })}
            </>
        )}
        </div>
    </div>;
}

export default ServicePersonsPage;
